/**
 * EVENTSFLO-CREW-PLANNER-SEND-SHIFT-ID-PACK-TO-CLIENT-V1
 * EVENTSFLO-ID-PACK-EMAIL-REVIEW-EDIT-BEFORE-SEND-V1
 *
 * "ID Pack" button + dialog for the Crew Planner expanded-shift panel.
 *
 * - Sits to the right of the "Crew (x/y)" header.
 * - Click opens a dialog that:
 *     1. Loads /api/planner/shifts/{id}/id-pack/preview
 *     2. Lets admin Download the PDF (always available)
 *     3. Renders an editable email draft (To, CC, Subject, Body) with a
 *        confidentiality-and-limited-purpose default body. Admin MUST
 *        review/edit before clicking Send to client.
 *     4. Surfaces missing-Photo-ID warnings and requires admin acknowledgement
 *        before send.
 *
 * Backend is the source of truth: the PDF is generated server-side and
 * Photo ID images are read from disk on the server. The frontend only
 * triggers the download / send.
 */
import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Textarea } from "../ui/textarea";
import { Loader2, FileText, Download, Mail, AlertTriangle, Shield } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

// EVENTSFLO-ID-PACK-EMAIL-REVIEW-EDIT-BEFORE-SEND-V1
// Default email subject and body. Includes the confidentiality / limited-
// purpose / authorised-sharing / no-redistribution / delete-when-done /
// contact-on-error wording required by the prompt.
const buildDefaultSubject = (preview) => {
  if (!preview) return "Crew ID Pack";
  const proj = (preview.project_name || "").trim() || "Project";
  const date = (preview.shift_date || "").trim();
  return `Crew ID Pack - ${proj}${date ? ` - ${date}` : ""}`;
};

const buildDefaultBody = (preview) => {
  if (!preview) return "";
  const proj = preview.project_name || "—";
  const shift = preview.shift_name || "—";
  const date = preview.shift_date || "—";
  const crewN = preview.crew_count || 0;
  const recipientName =
    (preview.client_contact_name && preview.client_contact_name.trim()) ||
    "team";
  const missingNote =
    preview.missing_id_count > 0
      ? `\nPlease note: ${preview.missing_id_count} crew member(s) currently have no Photo ID Front on file. They are clearly marked in the attached PDF.\n`
      : "";

  return (
`Hi ${recipientName},

Please find attached the Crew ID Pack for the following shift only:

  Project: ${proj}
  Shift:   ${shift}
  Date:    ${date}
  Crew:    ${crewN}
${missingNote}
Confidentiality and limited use
The crew members listed have authorised the sharing of these personal details, including their Photo ID Front documents, for the sole purpose of verification and site security/access control on this specific shift.

This email and the attached PDF contain confidential personal data. By accepting this material you agree to:
  - use it only for the stated shift verification / site security / access control purpose;
  - not redistribute, copy, forward, publish, or store the information beyond the operational necessity of this shift;
  - not use the information for any other purpose without our prior written permission;
  - securely delete or dispose of the file when it is no longer required for that shift / security purpose.

If you have any questions, or if you have received this email in error, please contact us immediately so we can arrange secure deletion.

Kind regards,
EventsFlo Operations`
  );
};

// Light, forgiving email validation — strict enough to catch obvious typos
// without rejecting valid-but-uncommon addresses.
const isLikelyEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "").trim());

const parseCcList = (s) =>
  String(s || "")
    .split(/[,;\n]+/)
    .map((x) => x.trim())
    .filter(Boolean);

export function IdPackButton({ shiftId, scale = 1 }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  // Editable email draft
  const [recipient, setRecipient] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [bodyDirty, setBodyDirty] = useState(false);
  const [subjectDirty, setSubjectDirty] = useState(false);

  const [ack, setAck] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setPreview(null);
    setAck(false);
    setCc("");
    setBodyDirty(false);
    setSubjectDirty(false);
    api.get(`/planner/shifts/${shiftId}/id-pack/preview`)
      .then((res) => {
        if (cancelled) return;
        setPreview(res.data);
        setRecipient(res.data?.client_email || "");
        setSubject(buildDefaultSubject(res.data));
        setBody(buildDefaultBody(res.data));
      })
      .catch((err) => {
        if (cancelled) return;
        const detail = err?.response?.data?.detail || err.message || "Preview failed";
        toast.error(`ID Pack preview failed: ${detail}`);
        setOpen(false);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, shiftId]);

  const handleDownload = async () => {
    setDownloading(true);
    const toastId = toast.loading("Generating ID Pack PDF…");
    try {
      const response = await api.get(`/planner/shifts/${shiftId}/id-pack.pdf`, {
        responseType: "blob",
      });
      let filename = "";
      const disposition = response.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/i.exec(disposition);
      if (m && m[1]) filename = m[1];
      if (!filename) filename = `Shift_${shiftId}_ID-Pack.pdf`;
      let blob = response.data;
      if (!(blob instanceof Blob)) {
        blob = new Blob([response.data], { type: "application/pdf" });
      }
      // EVENTSFLO-CREW-ID-PACK-IMAGE-COMPRESSION-FOR-EMAIL-V1:
      // surface PDF size from response headers so the admin sees how big
      // the optimised attachment is before they send it to the client.
      const pdfMb = parseFloat(response.headers?.["x-pack-pdf-mb"] || "0");
      const emailMb = parseFloat(response.headers?.["x-pack-email-estimate-mb"] || "0");
      const isLarge = response.headers?.["x-pack-email-large"] === "1";
      const sizeBlurb = pdfMb > 0
        ? ` (${pdfMb} MB PDF · ~${emailMb} MB email)`
        : "";
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      try { a.click(); } catch { window.location.href = blobUrl; }
      setTimeout(() => {
        try {
          if (a.parentNode) a.parentNode.removeChild(a);
          window.URL.revokeObjectURL(blobUrl);
        } catch { /* noop */ }
      }, 1000);
      // Persist size on local preview so the dialog can show it
      // immediately after the first download, before the user re-opens.
      if (pdfMb > 0) {
        setPreview((p) => p ? { ...p,
          pdf_size_mb: pdfMb,
          email_attachment_size_estimate_mb: emailMb,
          is_email_large: isLarge,
        } : p);
      }
      if (isLarge) {
        toast.warning?.(
          `ID Pack downloaded${sizeBlurb}. This may be too large for some email systems.`,
          { id: toastId, duration: 8000 }
        ) || toast.success(
          `ID Pack downloaded${sizeBlurb} — large file warning, see dialog.`,
          { id: toastId }
        );
      } else {
        toast.success(`ID Pack downloaded (${preview?.crew_count || 0} crew)${sizeBlurb}`, { id: toastId });
      }
    } catch (err) {
      // Blob error bodies need to be text-read
      let detail = "";
      try {
        const b = err?.response?.data;
        if (b && typeof b.text === "function") {
          const txt = await b.text();
          try { detail = JSON.parse(txt)?.detail || txt; } catch { detail = txt; }
        }
      } catch { /* noop */ }
      if (!detail) detail = err?.response?.data?.detail || err.message || "Unknown error";
      toast.error(`Download failed: ${detail}`, { id: toastId });
    } finally {
      setDownloading(false);
    }
  };

  const handleSend = async () => {
    const to = recipient.trim();
    if (!to || !isLikelyEmail(to)) {
      toast.error("Enter a valid recipient email before sending");
      return;
    }
    const ccList = parseCcList(cc);
    const badCc = ccList.find((e) => !isLikelyEmail(e));
    if (badCc) {
      toast.error(`CC address is not a valid email: ${badCc}`);
      return;
    }
    const finalSubject = subject.trim();
    if (!finalSubject) {
      toast.error("Subject cannot be empty");
      return;
    }
    if (!body.trim()) {
      toast.error("Email body cannot be empty");
      return;
    }
    if (preview?.missing_id_count > 0 && !ack) {
      toast.error(
        `${preview.missing_id_count} crew member(s) have no Photo ID Front. ` +
        `Tick the acknowledgement box to send anyway.`
      );
      return;
    }
    setSending(true);
    const toastId = toast.loading("Sending ID Pack to client…");
    try {
      const resp = await api.post(`/planner/shifts/${shiftId}/id-pack/send`, {
        recipient_email: to,
        cc_emails: ccList,
        subject: finalSubject,
        body,
        acknowledge_missing_ids: ack,
      });
      const summary = resp.data?.summary || {};
      const pdfMb = summary.pdf_size_mb;
      const emailMb = summary.email_attachment_size_estimate_mb;
      const isLarge = summary.is_email_large;
      const sizeBlurb = pdfMb ? ` (${pdfMb} MB PDF · ~${emailMb} MB email)` : "";
      if (isLarge) {
        (toast.warning || toast.success)(
          `ID Pack sent to ${resp.data?.recipient}${ccList.length ? ` (cc: ${ccList.length})` : ""}${sizeBlurb} — large attachment.`,
          { id: toastId, duration: 8000 }
        );
      } else {
        toast.success(
          `ID Pack sent to ${resp.data?.recipient}${ccList.length ? ` (cc: ${ccList.length})` : ""}${sizeBlurb}`,
          { id: toastId }
        );
      }
      setOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "";
      if (typeof detail === "string") msg = detail;
      else if (detail && typeof detail === "object") msg = detail.message || JSON.stringify(detail);
      else msg = err.message || "Unknown error";
      toast.error(`Send failed: ${msg}`, { id: toastId });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={`id-pack-btn-${shiftId}`}
        title="Generate a client-facing Crew ID Pack PDF (shift, venue, contacts, Photo ID Fronts)"
        style={{
          height: `${18 * scale}px`,
          fontSize: `${9 * scale}px`,
          padding: `0 ${6 * scale}px`,
          marginLeft: `${6 * scale}px`,
        }}
      >
        <FileText style={{ width: `${10 * scale}px`, height: `${10 * scale}px`, marginRight: `${3 * scale}px` }} />
        ID Pack
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="sm:max-w-2xl max-h-[92vh] overflow-y-auto"
          data-testid="id-pack-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Send Crew ID Pack to client
            </DialogTitle>
            <DialogDescription>
              Review and edit the email draft below before sending. The
              attached PDF contains shift / venue / client contacts and each
              assigned crew member&apos;s Photo ID Front. Costs, supplements,
              and internal notes are excluded.
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
            </div>
          )}

          {preview && !loading && (
            <div className="space-y-3 text-sm">
              <div className="bg-muted/30 rounded p-3 space-y-1 text-xs">
                <div><span className="text-muted-foreground">Project:</span> <span className="font-medium">{preview.project_name || "—"}</span></div>
                <div><span className="text-muted-foreground">Shift:</span> <span className="font-medium">{preview.shift_name || "—"}</span> · {preview.shift_date || "—"}</div>
                <div><span className="text-muted-foreground">Crew:</span> <span className="font-medium">{preview.crew_count}</span></div>
                <div><span className="text-muted-foreground">Client:</span> {preview.client_name || "—"}</div>
                {preview.pdf_size_mb ? (
                  <div data-testid="id-pack-size-info">
                    <span className="text-muted-foreground">PDF size:</span>{" "}
                    <span className="font-medium">{preview.pdf_size_mb} MB</span>
                    <span className="text-muted-foreground"> · email ~{preview.email_attachment_size_estimate_mb} MB</span>
                  </div>
                ) : null}
              </div>

              {/* EVENTSFLO-CREW-ID-PACK-IMAGE-COMPRESSION-FOR-EMAIL-V1
                  Soft warning shown when the optimised PDF is still large
                  enough that some corporate mailboxes may reject it.
                  Send is NOT blocked — admin can proceed with knowledge. */}
              {preview.is_email_large && (
                <div
                  className="rounded border border-amber-500/40 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs flex items-start gap-2"
                  data-testid="id-pack-large-warning"
                >
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                  <span className="text-amber-900 dark:text-amber-200">
                    This PDF is {preview.pdf_size_mb} MB ({preview.email_attachment_size_estimate_mb} MB after email
                    encoding). Some corporate email systems reject attachments larger than
                    {" "}~20 MB. You can still send — consider splitting the shift or sharing via a link if the
                    recipient bounces.
                  </span>
                </div>
              )}

              {/* EVENTSFLO-ID-PACK-EMAIL-REVIEW-EDIT-BEFORE-SEND-V1
                  Privacy banner — makes it explicit the admin is about to
                  send confidential personal ID data. */}
              <div className="rounded border border-blue-500/40 bg-blue-50 dark:bg-blue-900/20 p-3 text-xs flex items-start gap-2">
                <Shield className="h-4 w-4 mt-0.5 shrink-0 text-blue-600 dark:text-blue-400" />
                <span className="text-blue-900 dark:text-blue-200">
                  You are about to send confidential personal data (Photo ID
                  documents) to the client for this shift's verification /
                  site security / access control only. Please double-check the
                  recipient(s) and edit the email body below before sending.
                </span>
              </div>

              {preview.missing_id_count > 0 && (
                <div className="rounded border border-amber-500/40 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs space-y-2">
                  <div className="flex items-start gap-2 text-amber-700 dark:text-amber-400 font-medium">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      {preview.missing_id_count} crew member(s) have no Photo ID Front on file.
                      They will be marked clearly in the PDF, not silently omitted.
                    </span>
                  </div>
                  {preview.missing_ids_for?.length > 0 && (
                    <ul className="list-disc list-inside text-amber-800 dark:text-amber-300">
                      {preview.missing_ids_for.map((n) => (<li key={n}>{n}</li>))}
                    </ul>
                  )}
                  <label className="flex items-start gap-2 mt-2 cursor-pointer">
                    <Checkbox
                      checked={ack}
                      onCheckedChange={(v) => setAck(!!v)}
                      data-testid="id-pack-ack-missing"
                    />
                    <span className="text-xs">I acknowledge missing IDs and want to send anyway.</span>
                  </label>
                </div>
              )}

              {/* EVENTSFLO-CREW-ID-PACK-INCLUDE-ID-FRONT-BACK-AND-CREW-DETAILS-V1
                  Separate amber warning for missing Photo ID Back. Passport
                  holders are excluded server-side (back not applicable), so
                  this only fires for national-ID-style crew where the back
                  side is genuinely expected. */}
              {preview.missing_back_count > 0 && (
                <div
                  className="rounded border border-amber-500/40 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs space-y-2"
                  data-testid="id-pack-missing-back-warning"
                >
                  <div className="flex items-start gap-2 text-amber-700 dark:text-amber-400 font-medium">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      {preview.missing_back_count} crew member(s) have no Photo ID Back on file.
                      Each will be marked clearly on their page in the PDF.
                    </span>
                  </div>
                  {preview.missing_back_for?.length > 0 && (
                    <ul
                      className="list-disc list-inside text-amber-800 dark:text-amber-300"
                      data-testid="id-pack-missing-back-list"
                    >
                      {preview.missing_back_for.map((n) => (<li key={`back-${n}`}>{n}</li>))}
                    </ul>
                  )}
                </div>
              )}

              {/* ---------- Editable email draft ---------- */}
              <div className="space-y-2 border-t pt-3">
                <div className="space-y-1">
                  <Label htmlFor="id-pack-recipient" className="text-xs">
                    To <span className="text-muted-foreground">(client recipient)</span>
                  </Label>
                  <Input
                    id="id-pack-recipient"
                    type="email"
                    value={recipient}
                    onChange={(e) => setRecipient(e.target.value)}
                    placeholder="client@example.com"
                    data-testid="id-pack-recipient-input"
                  />
                  {!preview.client_email && (
                    <p className="text-xs text-amber-600">
                      No registered client contact email on file — please enter manually before sending.
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="id-pack-cc" className="text-xs">
                    CC <span className="text-muted-foreground">(comma-separated, optional)</span>
                  </Label>
                  <Input
                    id="id-pack-cc"
                    type="text"
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                    placeholder="ops@yourcompany.com, security@client.com"
                    data-testid="id-pack-cc-input"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="id-pack-subject" className="text-xs">Subject</Label>
                  <Input
                    id="id-pack-subject"
                    type="text"
                    value={subject}
                    onChange={(e) => { setSubject(e.target.value); setSubjectDirty(true); }}
                    data-testid="id-pack-subject-input"
                  />
                  {!subjectDirty && (
                    <p className="text-[10px] text-muted-foreground">
                      Default subject — edit if you want to override.
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="id-pack-body" className="text-xs">Email body</Label>
                    <button
                      type="button"
                      onClick={() => {
                        setBody(buildDefaultBody(preview));
                        setBodyDirty(false);
                      }}
                      className="text-[10px] underline text-muted-foreground hover:text-foreground"
                      data-testid="id-pack-body-reset"
                    >
                      Reset to default
                    </button>
                  </div>
                  <Textarea
                    id="id-pack-body"
                    value={body}
                    onChange={(e) => { setBody(e.target.value); setBodyDirty(true); }}
                    rows={14}
                    className="text-xs font-mono"
                    data-testid="id-pack-body-input"
                  />
                  {!bodyDirty && (
                    <p className="text-[10px] text-muted-foreground">
                      Default body includes the confidentiality, limited-purpose
                      (this shift only), authorised-sharing, no-redistribution,
                      and delete-when-done wording. Edit freely before sending.
                    </p>
                  )}
                </div>

                <p className="text-[10px] text-muted-foreground">
                  Attachment: the generated PDF (filename auto-set from project + shift + date) is attached automatically by the server.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={handleDownload}
              disabled={loading || downloading || !preview}
              data-testid="id-pack-download-btn"
            >
              {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
              Download PDF
            </Button>
            <Button
              onClick={handleSend}
              disabled={loading || sending || !preview || !recipient || !subject.trim() || !body.trim()}
              data-testid="id-pack-send-btn"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Mail className="h-4 w-4 mr-1" />}
              Send to client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
